import { ColumnDef } from '@tanstack/react-table';
import i18n from 'i18next';

import { Tag } from '@/components/generic';
import { TagValue } from '@/components/generic/Tag/tag-config';
import { Event, TableData } from '@/types';

export const getEventStatusLabel = (status?: string) => {
  if (!status) {
    return '';
  }
  // Pending now means "published, waiting for the device to pick it up". The device reports
  // Synchronizing itself once the transfer actually starts, so that label is no longer ours to use.
  if (status === TagValue.PENDING) {
    return TagValue.SYNCHRONIZATION_PENDING;
  }
  return status;
};

export const getEventsColumns: (allowedPermission: boolean) => ColumnDef<TableData>[] = (
  allowedPermission: boolean
) => {
  const defaultEventColumns: ColumnDef<TableData>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: i18n.t('name', { ns: 'common' }),
      enableSorting: true,
      cell: (props) => (
        <div className='flex justify-end gap-2 md:justify-start'>
          {props.getValue() as string}
          {(props.row.original as Event)?.status && (
            <div className='flex items-center justify-center md:hidden'>
              <Tag
                value={getEventStatusLabel((props.row.original as Event)?.status)}
                statusDescription={(props.row.original as Event)?.statusDescription}
              />
            </div>
          )}
        </div>
      )
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: i18n.t('description', { ns: 'common' }),
      enableSorting: true
    },
    {
      id: 'startDateAndTime',
      accessorKey: 'date',
      header: i18n.t('startDateAndTime', { ns: 'common' }),
      enableSorting: true,
      cell: (props) => (
        <div className='flex justify-end md:justify-start'>{props.getValue() as string}</div>
      )
    },
    {
      id: 'status',
      header: i18n.t('status', { ns: 'common' }),
      accessorKey: 'status',
      cell: (props) => (
        <div className={`${allowedPermission ? 'hidden md:flex' : 'flex'}`}>
          <Tag
            value={getEventStatusLabel(props.getValue() as string)}
            statusDescription={(props.row.original as Event)?.statusDescription}
          />
        </div>
      ),
      enableSorting: false
    }
  ];

  if (allowedPermission) {
    defaultEventColumns.push({
      id: 'organization',
      accessorKey: 'organization',
      header: i18n.t('organization', { ns: 'common' }),
      enableSorting: true,
      cell: (props) => {
        return (
          <div className='flex justify-end md:justify-start'>{props.getValue() as string}</div>
        );
      }
    });
  }

  return defaultEventColumns;
};

export const isValidDate = (date: Date | undefined) => {
  return date instanceof Date && !isNaN(date.getTime());
};
